import {useMutation} from '@apollo/client';
import {
  DeleteAccountDocument,
  InvestmentType,
  UpdateInvestmentAccountDocument,
} from '@transactions/graphql';
import * as React from 'react';
import {AccountClosingSection} from 'src/AccountClosingSection';
import {setAccounts} from 'src/apolloCache/updateAccounts';
import {Button} from 'src/Button';
import InputGroup from 'src/InputGroup';
import {TextInput} from 'src/inputs/TextInput';
import {LogoSelect} from 'src/LogoSelect';
import {Modal} from 'src/Modal';
import type {ApolloCache, FetchResult} from '@apollo/client';
import type {DeleteAccountMutation} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

// Minimal type for investment account - only fields needed by this modal
// Works with both InvestmentAccountFragment and AccountV2_InvestmentAccount_Fragment
type InvestmentAccountForEdit = {
  _id: string;
  name: string;
  investmentType: InvestmentType;
  logo?: string | null;
};

// Investment type options with human-readable labels
const INVESTMENT_TYPE_OPTIONS = [
  {value: InvestmentType.Brokerage, label: 'Brokerage'},
  {value: InvestmentType['401K'], label: '401(k)'},
  {value: InvestmentType.Ira, label: 'IRA'},
  {value: InvestmentType.RothIra, label: 'Roth IRA'},
  {value: InvestmentType.Hsa, label: 'HSA'},
  {value: InvestmentType.Education, label: '529 plan'},
  {value: InvestmentType.Other, label: 'Other'},
];

export type EditInvestmentAccountModalUserProps = {
  onUpdate?: () => void;
  onDelete?: (deleted: InvestmentAccountForEdit) => void;
  account: InvestmentAccountForEdit;
};

export const EditInvestmentAccountModal = (
  props: EditInvestmentAccountModalUserProps & SystemProps,
) => {
  const formId = React.useId();
  const {account, onRequestClose} = props;
  const [name, setName] = React.useState(account.name);
  const [investmentType, setInvestmentType] = React.useState(
    account.investmentType,
  );
  const [logo, setLogo] = React.useState(account.logo ?? '');
  const [updateAccount, {loading: updateLoading}] = useMutation(
    UpdateInvestmentAccountDocument,
  );
  const [deleteAccount, {loading: deleteLoading}] = useMutation(
    DeleteAccountDocument,
  );
  const [error, setError] = React.useState<string | null>(null);

  const hasChanges =
    name !== account.name ||
    investmentType !== account.investmentType ||
    logo !== (account.logo ?? '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    updateAccount({
      variables: {
        id: account._id,
        name,
        investmentType,
        logo: logo || null,
      },
    })
      .then(() => {
        props.onUpdate?.();
        onRequestClose();
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to save');
      });
  };

  const handleDelete = () => {
    deleteAccount({
      variables: {accountId: account._id},
      update: (
        store: ApolloCache<object>,
        result: FetchResult<DeleteAccountMutation>,
      ) => {
        const newAccounts = result?.data?.deleteAccount;
        if (newAccounts) {
          setAccounts(store, newAccounts);
        }
        // setAccounts only rewrites AllAccounts. The investments overview
        // reads a separate, cache-first allInvestmentAccounts query, so evict
        // the deleted account to drop its now-dangling row from every cached
        // list (otherwise it lingers, and tapping it hits a missing account).
        const id = store.identify({
          __typename: 'InvestmentAccount',
          _id: account._id,
        });
        if (id != null) {
          store.evict({id});
          store.gc();
        }
      },
    })
      .then(() => {
        props.onDelete?.(account);
        onRequestClose();
      })
      .catch((err: unknown) => {
        // Deletion can be refused server-side (e.g. an active Plaid
        // connection) — surface it instead of failing silently.
        setError(err instanceof Error ? err.message : 'Failed to delete');
      });
  };

  const handleInvestmentTypeChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const {value} = e.target;
    const validTypes: string[] = Object.values(InvestmentType);
    if (validTypes.includes(value)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      setInvestmentType(value as InvestmentType);
    }
  };

  const footer = (
    <>
      <Button
        variant="destructive"
        onClick={handleDelete}
        disabled={deleteLoading}
      >
        Delete
      </Button>
      <Button
        variant="outline"
        onClick={onRequestClose}
        disabled={updateLoading}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={!hasChanges || updateLoading}
      >
        Save
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Edit investment account"
      footer={footer}
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        <InputGroup label="Name">
          <TextInput autoFocus value={name} onChange={setName} />
        </InputGroup>
        <InputGroup label="Account type">
          <select value={investmentType} onChange={handleInvestmentTypeChange}>
            {INVESTMENT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </InputGroup>
        <LogoSelect value={logo} onChange={setLogo} />
        {error != null && (
          <div className="text-destructive text-sm">{error}</div>
        )}
        <AccountClosingSection accountId={account._id} />
      </form>
    </Modal>
  );
};
